import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { post } from "@common/api/apiShared";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useTranslation } from "@localization/useTranslation";
import { RiskTreatmentType } from "../../riskTreatmentType";
import { TreatmentDialogProps, RiskAcceptanceModel } from "./TreatmentDialogs.types";

export function useAcceptRiskDialog(props: TreatmentDialogProps) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    const { scenario } = props;

    const isCurrentlyAccepted = scenario.treatmentType === RiskTreatmentType.Accept;
    // A drifted acceptance can be cheaply re-based via reconfirm only when the risk is unchanged-or-better on
    // the same matrix; a worsened level or a changed scale must go through a fresh acceptance with new
    // justification, so reconfirm is never offered there.
    const showDriftBanner = isCurrentlyAccepted && scenario.isTreatmentDrifted;
    const acceptanceWorsened = scenario.acceptanceWorsened;
    const acceptanceScaleChanged = scenario.acceptanceScaleChanged;
    const canReconfirm = showDriftBanner && !acceptanceWorsened && !acceptanceScaleChanged;

    // Re-accepting an unchanged, already-accepted risk is a no-op the backend rejects — block the save so the
    // user never hits an error. Switching from another treatment, or re-accepting a drifted one, stays allowed.
    const isAlreadyAccepted = isCurrentlyAccepted && !scenario.isTreatmentDrifted;

    // A fresh decision is authored each time — the reason never carries forward from a prior treatment.
    const [reason, setReason] = useState("");
    const [reviewBy, setReviewBy] = useState<Date | null>(null);

    const reasonValid = !isNullOrWhitespace(reason);

    const acceptMutation = useDotLegalMutation((model: RiskAcceptanceModel) => post(`/risk-assessments/${scenario.id}/treatment/accept`, model));
    const reconfirmMutation = useDotLegalMutation(() => post(`/risk-assessments/${scenario.id}/treatment/reconfirm`, {}));

    const onSave = () => {
        if (!reasonValid) return;

        acceptMutation.mutateAsync(
            { reason: reason.trim(), reviewBy: reviewBy ? reviewBy.toISOString() : null },
            {
                onSuccess: () => {
                    snackbar.show(translateString("treatmentSaved"));
                    props.onSaved();
                },
            }
        );
    };

    const reconfirm = () =>
        reconfirmMutation.mutateAsync(undefined, {
            onSuccess: () => {
                snackbar.show(translateString("treatmentReconfirmed"));
                props.onSaved();
            },
        });

    return {
        showDriftBanner,
        canReconfirm,
        isAlreadyAccepted,
        reconfirm,
        reason,
        setReason,
        reviewBy,
        setReviewBy,
        okButtonDisabled: !reasonValid || isAlreadyAccepted,
        onSave,
        inProgress: acceptMutation.isLoading || reconfirmMutation.isLoading,
    };
}
